import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useBranding } from "@/hooks/useBranding";
import { useColorTheme } from "@/hooks/useColorTheme";

/** Resolve "auto" to the variant that reads on the current colour theme. */
const useLogoVariant = (variant) => {
  const { isLight } = useColorTheme();
  if (variant === "auto") return isLight ? "primary" : "dark";
  return variant;
};

/**
 * The organisation's own logo (white-label). `variant="primary"` is for light surfaces,
 * `variant="dark"` for navy / charcoal bands (the API serves a plated primary when no
 * dedicated dark logo was uploaded), `variant="auto"` (default) follows the active colour
 * theme. Falls back to a typographic lockup when the org hasn't uploaded a logo yet.
 */
export const BuilderLogo = ({
  variant: requested = "auto",
  className = "h-10",
  textClassName = "",
  testId = "builder-logo",
  alt,
  ...rest
}) => {
  const { branding, loaded, hasLogo, logoUrl, logoDarkUrl, shortName, tagline, name } = useBranding();
  const variant = useLogoVariant(requested);

  if (!loaded) {
    return <div className={cn(className, "w-28 animate-pulse rounded-md bg-muted/60")} aria-hidden="true" data-testid={`${testId}-loading`} />;
  }
  if (hasLogo) {
    return (
      <img
        src={variant === "dark" ? logoDarkUrl : logoUrl}
        alt={alt || `${name || shortName} logo`}
        className={cn(className, "w-auto object-contain")}
        data-testid={testId}
        data-variant={variant}
        {...rest}
      />
    );
  }
  return (
    <span
      className={cn("inline-flex flex-col justify-center leading-none", className, textClassName)}
      data-testid={testId}
      data-variant="text"
      {...rest}
    >
      <span className={cn("font-heading text-base font-bold tracking-[0.18em]", variant === "dark" ? "text-white" : "text-foreground")}>
        {(shortName || branding.name || "Your company").toUpperCase()}
      </span>
      {tagline && (
        <span className={cn("mt-1 text-[9px] font-semibold tracking-[0.3em]", variant === "dark" ? "text-white/60" : "text-primary")}>
          {tagline.toUpperCase()}
        </span>
      )}
    </span>
  );
};

/** Small square mark for the collapsed icon rail — the logo constrained to a square, following the theme. */
export const BuilderMark = ({ className = "h-9 w-9", testId = "builder-mark" }) => {
  const { loaded, hasLogo, logoUrl, logoDarkUrl, shortName, name } = useBranding();
  const variant = useLogoVariant("auto");
  if (!loaded) return <div className={cn(className, "animate-pulse rounded-lg bg-muted/60")} aria-hidden="true" />;
  if (hasLogo) {
    return <img src={variant === "dark" ? logoDarkUrl : logoUrl} alt={`${name || shortName} logo`} className={cn(className, "object-contain")} data-testid={testId} />;
  }
  const letters = (shortName || name || "B")
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  return (
    <span
      className={cn(className, "inline-flex items-center justify-center rounded-lg bg-primary font-heading text-xs font-bold text-primary-foreground")}
      data-testid={testId}
    >
      {letters}
    </span>
  );
};

/** Faint bottom-right watermark of the builder's logo on the app canvas (replaces the old fixed Group 5 artwork). */
export const BrandWatermark = ({ className }) => {
  const { loaded, hasLogo, logoUrl, logoDarkUrl } = useBranding();
  const variant = useLogoVariant("auto");
  if (!loaded || !hasLogo) return null;
  return (
    <div aria-hidden="true" className={cn("pointer-events-none fixed bottom-6 right-6 z-0 hidden select-none lg:block", className)} data-testid="brand-watermark">
      <img
        src={variant === "dark" ? logoDarkUrl : logoUrl}
        alt=""
        className="h-20 w-auto max-w-[320px] object-contain opacity-[0.07] grayscale"
        draggable={false}
      />
    </div>
  );
};

/** BuiltOS mark (SaaS master brand) — used on public pages and the Powered-by badge. */
export const BuiltOSMark = ({ className = "h-6 w-6", ...rest }) => (
  <img src="/brand/builtos-mark.svg" alt="" aria-hidden="true" className={cn(className, "select-none")} draggable={false} {...rest} />
);

/** BuiltOS lockup (mark + wordmark) for the sign-in / landing pages. */
export const BuiltOSLockup = ({ className = "h-12", tone = "auto", testId = "builtos-lockup" }) => (
  <img
    src={tone === "light" ? "/brand/builtos-lockup-light.svg" : "/brand/builtos-lockup.svg"}
    alt="BuiltOS"
    className={cn(className, "w-auto select-none object-contain")}
    draggable={false}
    data-testid={testId}
  />
);

/**
 * BuiltOS text wordmark for the app's icon rail: the navy mark + "Built" (navy on light
 * themes, foreground on dark) + "OS" in steel blue. `compact` renders the mark only
 * (collapsed rail). Colours follow the BuiltOS navy / steel-blue palette.
 */
export const BuiltOSWordmark = ({ compact = false, className, testId = "builtos-wordmark" }) => {
  const { isLight } = useColorTheme();
  return (
    <span
      className={cn("inline-flex items-center gap-2.5 select-none", className)}
      data-testid={testId}
      data-compact={compact ? "true" : "false"}
      aria-label="BuiltOS"
    >
      <BuiltOSMark className="h-8 w-8 shrink-0 rounded-[9px] shadow-sm" />
      {!compact && (
        <span className="font-heading text-[19px] font-bold leading-none tracking-[-0.02em]" data-testid={`${testId}-text`}>
          <span className={isLight ? "text-[hsl(var(--builtos-navy))]" : "text-foreground"}>Built</span>
          <span className="text-[hsl(var(--builtos-steel))]">OS</span>
        </span>
      )}
    </span>
  );
};

/**
 * Co-branding badge: "Powered by BuiltOS". `variant="rail"` is icon-only for the 64px rail,
 * `variant="row"` is the default text badge for panels, footers and mobile menus,
 * `variant="public"` is tuned for dark public / portal pages.
 */
export const PoweredByBuiltOS = ({ variant = "row", className, testId = "powered-by-builtos" }) => {
  const { poweredBy } = useBranding();
  const to = poweredBy?.url || "/welcome";
  if (variant === "rail") {
    return (
      <Link
        to={to}
        aria-label="Powered by BuiltOS"
        className={cn("flex h-8 w-8 items-center justify-center rounded-lg opacity-70 transition-opacity duration-150 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", className)}
        data-testid={testId}
      >
        <BuiltOSMark className="h-5 w-5" />
      </Link>
    );
  }
  const dark = variant === "public";
  return (
    <Link
      to={to}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-medium tracking-wide transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        dark
          ? "border-white/10 text-stone-500 hover:border-white/20 hover:text-stone-300"
          : "border-border text-muted-foreground hover:border-primary/40 hover:text-foreground",
        className
      )}
      data-testid={testId}
    >
      <BuiltOSMark className="h-3.5 w-3.5" />
      <span>
        Powered by <span className={cn("font-semibold", dark ? "text-stone-300" : "text-foreground")}>{poweredBy?.name || "BuiltOS"}</span>
      </span>
    </Link>
  );
};
